import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { quotesApi, type QuoteListItemDto, type ListQuotesParams, type QuoteStatus } from "@/lib/quotes";
import { approvedQuotesApi, type ApprovedQuoteListItem } from "@/lib/approved-quotes";
import { QuoteStatusBadge } from "@/components/quotes/quote-status-badge";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/app/auth-context";
import { NewQuoteModal } from "./NewQuoteModal";
import { Plus } from "lucide-react";
import { formatDate } from "@/lib/format";

// ---------------------------------------------------------------------------
// Filter state — single search + chip-based status grouping
// ---------------------------------------------------------------------------

type StatusChipId = "active" | "approved" | "converted" | "archived" | "all";

interface FilterState {
  q: string;
  chip: StatusChipId;
}

const EMPTY_FILTERS: FilterState = { q: "", chip: "active" };

const STATUS_CHIPS = [
  { id: "active" as const,    label: "Show Active" },
  { id: "approved" as const,  label: "Approved" },
  { id: "converted" as const, label: "Converted" },
  { id: "archived" as const,  label: "Archived" },
  { id: "all" as const,       label: "All" },
];

const STATUS_GROUPS: Record<StatusChipId, QuoteStatus[] | undefined> = {
  active:    ["Draft", "Sent"],
  approved:  ["Approved"],
  converted: ["Converted"],
  archived:  ["Rejected", "Expired"],
  all:       undefined,
};

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// Build API params from filter state
// ---------------------------------------------------------------------------

function buildParams(
  state: FilterState,
  pageIndex: number,
  pageSize: number,
  clientId: number | undefined,
): ListQuotesParams {
  return {
    q: state.q || undefined,
    status: STATUS_GROUPS[state.chip],
    clientId,
    page: pageIndex,
    pageSize,
  };
}

// ---------------------------------------------------------------------------
// Adapter — ApprovedQuoteListItem → QuoteListItemDto
// ---------------------------------------------------------------------------

function approvedRowToQuoteListItem(a: ApprovedQuoteListItem): QuoteListItemDto {
  return {
    quoteId:           a.quoteId,
    quoteRef:          a.quoteRef,
    status:            "Approved",
    description:       a.description ?? null,
    clientId:          a.clientId ?? null,
    clientName:        a.clientDisplay ?? null,
    ownerEmployeeId:   a.ownerEmployeeId ?? null,
    ownerEmployeeName: a.ownerEmployeeName ?? null,
    branchId:          null,
    totalIncGst:       a.totalIncGst ?? null,
    validUntil:        a.validUntil ?? null,
    quoteVersionNo:    1,
    createdOn:         null,
    enquiryId:         null,
  };
}

// ---------------------------------------------------------------------------
// QuotesListPage
// ---------------------------------------------------------------------------

export function QuotesListPage() {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [newQuoteModalOpen, setNewQuoteModalOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdParam = searchParams.get("clientId");
  // Coerce defensively: NaN, 0, and negatives all collapse to undefined so
  // the chip never reads "Client #NaN" and we never send a bogus filter.
  const parsedClientId = clientIdParam ? Number(clientIdParam) : NaN;
  const clientIdFilter =
    Number.isFinite(parsedClientId) && parsedClientId > 0 ? parsedClientId : undefined;
  const clearClientFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("clientId");
    setSearchParams(next);
    setPageIndex(0);
  };

  const params = useMemo(
    () => buildParams(filters, pageIndex, pageSize, clientIdFilter),
    [filters, pageIndex, pageSize, clientIdFilter],
  );

  const isApprovedChip = filters.chip === "approved";

  const quotesQuery = useQuery({
    queryKey: ["quotes", params],
    queryFn: () => quotesApi.list(params),
    enabled: !isApprovedChip,
  });

  const approvedQuery = useQuery({
    queryKey: ["approved-quotes", pageIndex, pageSize],
    queryFn: () => approvedQuotesApi.list(pageIndex + 1, pageSize),
    enabled: isApprovedChip,
  });

  const data = isApprovedChip
    ? {
        items:      (approvedQuery.data?.items ?? []).map(approvedRowToQuoteListItem),
        totalCount: approvedQuery.data?.totalCount ?? 0,
      }
    : quotesQuery.data;

  const isLoading = isApprovedChip ? approvedQuery.isLoading : quotesQuery.isLoading;

  // Sort state — client-side only; backend (quotesApi.list) has no `sort` param.
  // Sort applies to the current page's rows, not the full result set.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const visibleRows = useMemo(() => {
    const rows = data?.items ?? [];
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: QuoteListItemDto): string | number | null => {
      switch (sort.columnId) {
        case "ref":         return r.quoteRef;
        case "description": return r.description ?? null;
        case "client":      return r.clientName ?? null;
        case "owner":       return r.ownerEmployeeName ?? null;
        case "total":       return r.totalIncGst ?? null;
        case "status":      return r.status;
        case "validUntil":  return r.validUntil ?? null;
        case "version":     return r.quoteVersionNo;
        default:            return null;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort]);

  const cols: DataGridColumn<QuoteListItemDto>[] = [
    {
      id: "ref",
      header: "Quote Ref",
      sortable: true,
      accessor: (r) => <span className="font-medium text-slate-900">{r.quoteRef}</span>,
    },
    {
      id: "description",
      header: "Description",
      sortable: true,
      accessor: (r) => r.description ?? "—",
    },
    {
      id: "client",
      header: "Client",
      sortable: true,
      accessor: (r) =>
        r.clientId ? (
          <Link to={`/clients/${r.clientId}`} className="underline text-primary hover:text-primary/80 transition-colors">
            {r.clientName ?? "—"}
          </Link>
        ) : (
          r.clientName ?? "—"
        ),
    },
    {
      id: "owner",
      header: "Owner",
      sortable: true,
      accessor: (r) => r.ownerEmployeeName ?? "—",
    },
    {
      id: "total",
      header: "Total inc-GST",
      sortable: true,
      accessor: (r) => (r.totalIncGst != null ? fmt.format(r.totalIncGst) : "—"),
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      accessor: (r) => <QuoteStatusBadge status={r.status} />,
    },
    {
      id: "validUntil",
      header: "Valid until",
      sortable: true,
      accessor: (r) =>
        r.validUntil ? formatDate(r.validUntil) : "—",
    },
    {
      id: "version",
      header: "Version",
      sortable: true,
      accessor: (r) => `v${r.quoteVersionNo}`,
    },
  ];

  return (
    <ListPage
      title="Quotes"
      primaryAction={
        permissions.has("quotes:write") ? (
          <Button onClick={() => setNewQuoteModalOpen(true)} aria-label="New quote">
            <Plus className="mr-1.5 h-4 w-4" /> Add Quote
          </Button>
        ) : undefined
      }
      search={{
        value: filters.q,
        onChange: (v) => { setFilters((f) => ({ ...f, q: v })); setPageIndex(0); },
        placeholder: "Search ref, customer, description…",
      }}
      filterChips={{
        options: STATUS_CHIPS,
        value: filters.chip,
        onChange: (next) => { setFilters((f) => ({ ...f, chip: next })); setPageIndex(0); },
        "aria-label": "Show quotes by status",
      }}
      filterBar={
        clientIdFilter != null ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1 text-xs">
              Filtered by Client #{clientIdFilter}
              <button
                type="button"
                onClick={clearClientFilter}
                aria-label="Clear client filter"
                className="text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </span>
          </div>
        ) : undefined
      }
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.quoteId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={pageIndex}
        pageSize={pageSize}
        loading={isLoading}
        emptyHeadline="No quotes match these filters."
        onPageChange={setPageIndex}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
        onSortChange={setSort}
        onRowClick={(r) => navigate(`/quotes/${r.quoteId}`)}
      />
      <NewQuoteModal open={newQuoteModalOpen} onOpenChange={setNewQuoteModalOpen} />
    </ListPage>
  );
}
